import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Banknote,
  CreditCard,
  Clock3,
  Loader2,
  NotebookPen,
  PhoneCall,
  Plus,
  UserRound,
  UserX,
} from "lucide-react";
import { api, formatApiError, money } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";

const STATUS = {
  pending: { label: "Bekliyor", className: "bg-secondary text-muted-foreground" },
  confirmed: { label: "Onaylı", className: "bg-primary/10 text-primary" },
  completed: { label: "Tamamlandı", className: "bg-primary/10 text-primary" },
  no_show: { label: "Gelmedi", className: "bg-secondary text-muted-foreground" },
};

const PAYMENT_METHODS = [
  { id: "cash", label: "Nakit", icon: Banknote },
  { id: "card", label: "Kart", icon: CreditCard },
  { id: "credit", label: "Veresiye", icon: NotebookPen },
];

const isoDate = (value) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const dayStrip = () => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  return Array.from({ length: 14 }, (_, index) => {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    return date;
  });
};

export default function AppointmentAgenda() {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(isoDate(new Date()));
  const [dialog, setDialog] = useState(null);
  const [actionReservation, setActionReservation] = useState(null);
  const [form, setForm] = useState({
    service_id: "",
    staff_id: "",
    customer_name: "",
    customer_phone: "",
    date: selectedDate,
    time: "",
  });
  const [saving, setSaving] = useState(false);
  const dates = useMemo(dayStrip, []);

  const { data: reservations = [] } = useQuery({
    queryKey: ["reservations"],
    queryFn: () => api.get("/reservations").then((response) => response.data),
  });
  const { data: agenda, isLoading } = useQuery({
    queryKey: ["agenda", selectedDate],
    queryFn: () => api.get("/reservations/agenda", { params: { date: selectedDate } }).then((response) => response.data),
  });
  const bookingDate = form.date || selectedDate;
  const { data: bookingAgenda, isFetching: loadingBookingAgenda } = useQuery({
    queryKey: ["agenda", bookingDate, "manual-booking"],
    queryFn: () => api.get("/reservations/agenda", { params: { date: bookingDate } })
      .then((response) => response.data),
    enabled: Boolean(dialog),
  });
  const { data: bookingAvailability, isFetching: loadingBookingAvailability } = useQuery({
    queryKey: [
      "owner-available-slots",
      bookingDate,
      form.service_id,
      form.staff_id,
      dialog?.reservationId,
    ],
    queryFn: () => api.get("/reservations/available-slots", {
      params: {
        service_id: form.service_id,
        date: bookingDate,
        staff_id: form.staff_id || undefined,
        exclude_id: dialog?.reservationId || undefined,
      },
    }).then((response) => response.data),
    enabled: Boolean(dialog && form.service_id && bookingDate),
  });

  const dayReservations = useMemo(() => {
    if (!agenda?.slots) return [];
    return agenda.slots.flatMap((slot) => slot.reservations || []);
  }, [agenda]);
  const waitingCount = dayReservations.filter((reservation) => reservation.status === "pending").length;
  const doneCount = dayReservations.filter((reservation) => reservation.status === "completed").length;
  const revenue = dayReservations
    .filter((reservation) => reservation.status === "completed")
    .reduce((sum, reservation) => sum + Number(reservation.service_price || 0), 0);
  const busyByDate = useMemo(() => {
    return reservations.reduce((map, reservation) => {
      if (!["cancelled", "no_show"].includes(reservation.status)) {
        map[reservation.date] = (map[reservation.date] || 0) + 1;
      }
      return map;
    }, {});
  }, [reservations]);
  const freeSlots = (agenda?.slots || []).filter((slot) => slot.status === "free");
  const selected = new Date(`${selectedDate}T00:00:00`);
  const isToday = selectedDate === isoDate(new Date());
  const bookingServices = bookingAgenda?.services || agenda?.services || [];
  const bookingStaff = bookingAgenda?.staff || agenda?.staff || [];
  const selectedService = bookingServices.find((service) => service.id === form.service_id);
  const availableBookingSlots = bookingAvailability?.slots || [];
  const customerSuggestions = useMemo(() => {
    const knownCustomers = new Map();
    reservations.forEach((reservation) => {
      const name = reservation.customer_name?.trim();
      if (!name || knownCustomers.has(name.toLocaleLowerCase("tr-TR"))) return;
      knownCustomers.set(name.toLocaleLowerCase("tr-TR"), {
        name,
        phone: reservation.customer_phone || "",
      });
    });
    return [...knownCustomers.values()].slice(0, 6);
  }, [reservations]);
  const quickDays = useMemo(() => {
    const current = new Date();
    current.setHours(0, 0, 0, 0);
    return [0, 1, 2].map((offset) => {
      const date = new Date(current);
      date.setDate(current.getDate() + offset);
      return {
        value: isoDate(date),
        label: offset === 0
          ? "Bugün"
          : offset === 1
            ? "Yarın"
            : date.toLocaleDateString("tr-TR", { day: "numeric", month: "short" }),
      };
    });
  }, []);

  const openManualBooking = (time) => {
    const firstService = agenda?.services?.[0];
    setForm({
      service_id: firstService?.id || "",
      staff_id: "",
      customer_name: "",
      customer_phone: "",
      date: selectedDate,
      time,
    });
    setDialog({ source: "slot" });
  };

  const openNewBooking = () => {
    const firstService = agenda?.services?.[0];
    setForm({
      service_id: firstService?.id || "",
      staff_id: "",
      customer_name: "",
      customer_phone: "",
      date: selectedDate,
      time: "",
    });
    setDialog({ source: "quick-add" });
  };

  const updateStatus = async (reservationId, status) => {
    try {
      await api.patch(`/reservations/${reservationId}`, { status });
      queryClient.invalidateQueries({ queryKey: ["agenda", selectedDate] });
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      toast.success("Randevu durumu güncellendi");
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const recordPayment = async (payment_method) => {
    if (!actionReservation) return;
    try {
      await api.patch(`/reservations/${actionReservation.id}/payment`, { payment_method });
      queryClient.invalidateQueries({ queryKey: ["agenda"] });
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      toast.success("Ödeme kaydedildi ve randevu tamamlandı");
      setActionReservation(null);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const markNoShow = async () => {
    if (!actionReservation) return;
    await updateStatus(actionReservation.id, "no_show");
    setActionReservation(null);
  };

  const openEditReservation = () => {
    if (!actionReservation) return;
    setForm({
      service_id: actionReservation.service_id || "",
      staff_id: actionReservation.staff_id || "",
      customer_name: actionReservation.customer_name || "",
      customer_phone: actionReservation.customer_phone || "",
      date: actionReservation.date || selectedDate,
      time: actionReservation.time || "",
    });
    setActionReservation(null);
    setDialog({ source: "edit", reservationId: actionReservation.id });
  };

  const saveManualBooking = async () => {
    if (!form.service_id || !form.customer_name.trim() || !form.date || !form.time) {
      toast.error("Müşteri, gün, saat ve hizmet seçin");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        customer_name: form.customer_name.trim(),
        customer_phone: form.customer_phone.trim(),
        staff_id: form.staff_id || undefined,
      };
      if (dialog?.reservationId) {
        await api.patch(`/reservations/${dialog.reservationId}/details`, payload);
      } else {
        await api.post("/reservations/manual", payload);
      }
      queryClient.invalidateQueries({ queryKey: ["agenda"] });
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
      toast.success(dialog?.reservationId ? "Randevu güncellendi" : "Randevu eklendi");
      setDialog(null);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 pb-6" data-testid="appointment-dashboard">
      <section className="animate-in fade-in slide-in-from-bottom-2 duration-500">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-amber-400">
          {isToday ? "Bugünün ajandası" : "Günlük ajanda"}
        </p>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-display text-4xl font-bold text-white sm:text-5xl">
              {isToday ? "Bugün" : selected.toLocaleDateString("tr-TR", { day: "numeric", month: "long" })}
            </h1>
            <p className="mt-1 text-sm text-slate-400" data-testid="appointment-count-text">
              {dayReservations.length} randevu · {doneCount} tamamlandı · {waitingCount} bekliyor
            </p>
          </div>
          <div className="rounded-lg border border-slate-700 bg-slate-900 px-4 py-3 text-right">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Bugün alınan</p>
            <p className="font-display text-2xl font-bold text-emerald-300" data-testid="daily-revenue">
              {money(revenue)}
            </p>
          </div>
          <Button
            onClick={openNewBooking}
            className="bg-amber-400 text-slate-950 hover:bg-amber-300"
            data-testid="add-appointment-button"
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Randevu
          </Button>
        </div>
      </section>

      <section className="-mx-5 overflow-x-auto px-5 pb-1 sm:-mx-8 sm:px-8" data-testid="date-strip">
        <div className="flex min-w-max gap-2">
          {dates.map((date) => {
            const value = isoDate(date);
            const active = value === selectedDate;
            const count = busyByDate[value] || 0;
            return (
              <button
                key={value}
                type="button"
                onClick={() => setSelectedDate(value)}
                className={cn(
                  "flex h-[68px] w-[54px] flex-col items-center justify-center rounded-xl border text-xs "
                  + "transition-colors duration-200",
                  active
                    ? "border-amber-400 bg-amber-400 text-slate-950"
                    : "border-slate-800 bg-slate-900 text-slate-300 hover:border-slate-600",
                )}
                data-testid={`agenda-date-${value}`}
              >
                <span className="text-[10px] font-semibold uppercase">
                  {date.toLocaleDateString("tr-TR", { weekday: "short" }).slice(0, 3)}
                </span>
                <span className="font-display text-xl font-bold leading-tight">{date.getDate()}</span>
                <span className={cn("mt-1 h-1.5 w-1.5 rounded-full", count ? "bg-current" : "bg-transparent")} />
              </button>
            );
          })}
        </div>
      </section>

      {isLoading ? (
        <div className="flex justify-center py-20" data-testid="agenda-loading">
          <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
        </div>
      ) : agenda?.closed ? (
        <div className="border border-dashed border-slate-700 px-6 py-14 text-center text-slate-400" data-testid="agenda-closed">
          Bu gün işletme kapalı.
        </div>
      ) : (
        <>
          <section data-testid="daily-appointment-list">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-display text-2xl font-bold text-white">Randevular</h2>
              <span className="text-xs text-slate-500">{agenda?.open} – {agenda?.close}</span>
            </div>
            {dayReservations.length === 0 ? (
              <div className="border border-dashed border-slate-700 px-6 py-10 text-center text-sm text-slate-400" data-testid="no-day-appointments">
                Bu gün için henüz randevu yok.
              </div>
            ) : (
              <div className="divide-y divide-slate-800 border-y border-slate-800">
                {dayReservations.map((reservation) => {
                  const status = STATUS[reservation.status] || STATUS.pending;
                  return (
                    <article
                      key={reservation.id}
                      onDoubleClick={() => setActionReservation(reservation)}
                      className={
                        "select-none grid grid-cols-[64px_1fr_auto] gap-3 py-4 "
                        + "sm:grid-cols-[82px_1fr_auto]"
                      }
                      data-testid={`agenda-reservation-${reservation.id}`}
                    >
                      <div className="font-display text-xl font-bold text-amber-300">{reservation.time}</div>
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-100">{reservation.customer_name}</p>
                        <p className="mt-0.5 truncate text-sm text-slate-400">
                          {reservation.service_name}{reservation.staff_name ? ` · ${reservation.staff_name}` : ""}
                        </p>
                        {reservation.customer_phone && (
                          <a
                            href={`tel:${reservation.customer_phone}`}
                            className="mt-1 inline-flex text-xs text-slate-500 hover:text-amber-300"
                            data-testid={`agenda-phone-${reservation.id}`}
                          >
                            {reservation.customer_phone}
                          </a>
                        )}
                      </div>
                      <div className="flex min-w-[98px] flex-col items-end gap-2">
                        <span className="font-semibold text-slate-100">{money(reservation.service_price)}</span>
                        <span className={cn("rounded-full px-2 py-1 text-[10px] font-bold", status.className)}>
                          {status.label}
                        </span>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <section data-testid="empty-slots-section">
            <div className="mb-3 flex items-center gap-2">
              <Clock3 className="h-5 w-5 text-amber-400" />
              <div>
                <h2 className="font-display text-2xl font-bold text-white">Boş saatler</h2>
                <p className="text-xs text-slate-500">Dokunun, randevu yazın</p>
              </div>
            </div>
            {freeSlots.length === 0 ? (
              <p className="text-sm text-slate-400" data-testid="no-free-slots">Müsait saat bulunmuyor.</p>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-5" data-testid="empty-slots-grid">
                {freeSlots.map((slot) => (
                  <button
                    key={slot.time}
                    type="button"
                    onClick={() => openManualBooking(slot.time)}
                    className="flex items-center justify-center gap-1 rounded-lg border border-slate-700 bg-slate-900 px-2 py-3 font-display text-lg font-bold text-slate-200 transition-colors duration-200 hover:border-amber-400 hover:text-amber-300"
                    data-testid={`empty-slot-button-${slot.time}`}
                  >
                    {slot.time}
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      <Dialog open={Boolean(dialog)} onOpenChange={(value) => !value && setDialog(null)}>
        <DialogContent
          className="business-panel-light max-h-[90vh] overflow-y-auto"
          data-testid="manual-booking-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-slate-100">
              {dialog?.reservationId ? "Randevuyu Düzenle" : "Yeni Randevu"}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Randevu yazılmadan önce tüm bilgileri kontrol edin.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <section>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Müşteri</p>
              <Input
                value={form.customer_name}
                onChange={(event) => setForm((value) => ({ ...value, customer_name: event.target.value }))}
                placeholder="Müşteri adı"
                className="border-slate-700 bg-slate-900 text-slate-100"
                data-testid="manual-name"
              />
              {customerSuggestions.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="manual-customer-suggestions">
                  {customerSuggestions.map((customer) => (
                    <button
                      key={customer.name}
                      type="button"
                      onClick={() => setForm((value) => ({
                        ...value,
                        customer_name: customer.name,
                        customer_phone: customer.phone || value.customer_phone,
                      }))}
                      className="rounded-full bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-200 hover:bg-slate-700"
                      data-testid={`manual-customer-${customer.name}`}
                    >
                      {customer.name}
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Telefon (isteğe bağlı)</p>
              <Input
                value={form.customer_phone}
                onChange={(event) => setForm((value) => ({ ...value, customer_phone: event.target.value }))}
                placeholder="05xx xxx xx xx"
                className="border-slate-700 bg-slate-900 text-slate-100"
                data-testid="manual-phone"
              />
            </section>

            <section>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Gün</p>
              <div className="flex flex-wrap gap-2">
                {quickDays.map((day) => (
                  <button
                    key={day.value}
                    type="button"
                    onClick={() => setForm((value) => ({ ...value, date: day.value, time: "" }))}
                    className={cn(
                      "rounded-lg border px-3 py-2 text-sm font-semibold transition-colors duration-200",
                      form.date === day.value
                        ? "border-amber-400 bg-amber-400 text-slate-950"
                        : "border-slate-700 bg-slate-900 text-slate-200 hover:border-slate-500",
                    )}
                    data-testid={`manual-day-${day.value}`}
                  >
                    {day.label}
                  </button>
                ))}
                <Input
                  type="date"
                  value={form.date}
                  min={isoDate(new Date())}
                  onChange={(event) => setForm((value) => ({
                    ...value,
                    date: event.target.value,
                    time: "",
                  }))}
                  className="h-10 w-auto border-slate-700 bg-slate-900 text-slate-200"
                  aria-label="Başka gün seç"
                  data-testid="manual-date-input"
                />
              </div>
            </section>

            <section>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Saat</p>
              {loadingBookingAgenda || loadingBookingAvailability ? (
                <Loader2 className="h-5 w-5 animate-spin text-amber-400" data-testid="manual-slots-loading" />
              ) : availableBookingSlots.length === 0 ? (
                <p className="text-sm text-slate-400" data-testid="manual-no-slots">Bu gün için uygun saat yok.</p>
              ) : (
                <div className="grid grid-cols-4 gap-2" data-testid="manual-time-grid">
                  {availableBookingSlots.map((time) => (
                    <button
                      key={time}
                      type="button"
                      onClick={() => setForm((value) => ({ ...value, time }))}
                      className={cn(
                        "rounded-lg border px-2 py-2 text-sm font-semibold transition-colors duration-200",
                        form.time === time
                          ? "border-amber-400 bg-amber-400 text-slate-950"
                          : "border-slate-700 bg-slate-900 text-slate-200 hover:border-slate-500",
                      )}
                      data-testid={`manual-time-${time}`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Ne yapılacak</p>
              <div className="grid gap-2 sm:grid-cols-2" data-testid="manual-services-grid">
                {bookingServices.map((service) => (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() => setForm((value) => ({
                      ...value,
                      service_id: service.id,
                      time: "",
                    }))}
                    className={cn(
                      "flex items-center justify-between rounded-lg border px-3 py-2.5 text-left transition-colors duration-200",
                      form.service_id === service.id
                        ? "border-amber-400 bg-amber-400/15 text-primary"
                        : "border-slate-700 bg-slate-900 text-slate-200 hover:border-slate-500",
                    )}
                    data-testid={`manual-service-${service.id}`}
                  >
                    <span className="font-semibold">{service.name}</span>
                    <span className="text-sm">{money(service.price)}</span>
                  </button>
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-slate-800 bg-slate-900 px-4 py-3">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Ücret</p>
              <p
                className="mt-1 font-display text-2xl font-bold text-primary"
                data-testid="manual-service-price"
              >
                {selectedService ? money(selectedService.price) : "Hizmet seçin"}
              </p>
            </section>

            <section>
              <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Kim yapacak</p>
              <div className="flex flex-wrap gap-2" data-testid="manual-staff-list">
                <button
                  type="button"
                  onClick={() => setForm((value) => ({ ...value, staff_id: "" }))}
                  className={cn(
                    "rounded-lg border px-3 py-2 text-sm font-semibold transition-colors duration-200",
                    !form.staff_id
                      ? "border-amber-400 bg-amber-400 text-slate-950"
                      : "border-slate-700 bg-slate-900 text-slate-200 hover:border-slate-500",
                  )}
                  data-testid="manual-staff-any"
                >
                  Fark etmez
                </button>
                {bookingStaff.map((person) => (
                  <button
                    key={person.id}
                    type="button"
                    onClick={() => setForm((value) => ({
                      ...value,
                      staff_id: person.id,
                      time: "",
                    }))}
                    className={cn(
                      "rounded-lg border px-3 py-2 text-sm font-semibold transition-colors duration-200",
                      form.staff_id === person.id
                        ? "border-amber-400 bg-amber-400 text-slate-950"
                        : "border-slate-700 bg-slate-900 text-slate-200 hover:border-slate-500",
                    )}
                    data-testid={`manual-staff-${person.id}`}
                  >
                    <UserRound className="mr-1 inline h-3.5 w-3.5" />
                    {person.name}
                  </button>
                ))}
              </div>
            </section>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} data-testid="manual-cancel">Vazgeç</Button>
            <Button
              onClick={saveManualBooking}
              disabled={saving}
              className="bg-amber-400 text-slate-950 hover:bg-amber-300"
              data-testid="manual-submit"
            >
              {saving
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : dialog?.reservationId ? "Kaydet" : "Randevuyu Yaz"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(actionReservation)}
        onOpenChange={(value) => !value && setActionReservation(null)}
      >
        <DialogContent
          className="business-panel-light max-h-[90vh] overflow-y-auto"
          data-testid="appointment-actions-dialog"
        >
          <DialogHeader>
            <DialogTitle>{actionReservation?.customer_name}</DialogTitle>
            <DialogDescription>
              {actionReservation?.date} · {actionReservation?.time} · {actionReservation?.service_name}
              {actionReservation?.staff_name ? ` · ${actionReservation.staff_name}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {actionReservation?.customer_phone ? (
              <a
                href={`tel:${actionReservation.customer_phone}`}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg border border-border text-sm font-semibold hover:bg-secondary"
                data-testid="appointment-action-call"
              >
                <PhoneCall className="h-4 w-4" />
                Ara
              </a>
            ) : (
              <Button variant="outline" disabled data-testid="appointment-action-call-disabled">
                <PhoneCall className="mr-2 h-4 w-4" />
                Telefon yok
              </Button>
            )}
            {!["no_show", "cancelled"].includes(actionReservation?.status) && (
              <Button
                variant="outline"
                onClick={openEditReservation}
                data-testid="appointment-action-edit"
              >
                <NotebookPen className="mr-2 h-4 w-4" />
                Düzenle
              </Button>
            )}
          </div>
          {!["no_show", "cancelled"].includes(actionReservation?.status) && (
            <section className="rounded-lg border border-border bg-secondary/40 p-4">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Alınan ücret</p>
              <p className="mt-1 font-display text-3xl font-bold" data-testid="appointment-action-amount">
                {money(actionReservation?.service_price)}
              </p>
              <div className="mt-4 grid grid-cols-3 gap-2">
                {PAYMENT_METHODS.map((method) => {
                  const Icon = method.icon;
                  const selected = actionReservation?.payment_method === method.id;
                  return (
                    <Button
                      key={method.id}
                      variant={selected ? "default" : "outline"}
                      onClick={() => recordPayment(method.id)}
                      className="h-auto flex-col gap-1 py-3"
                      data-testid={`appointment-payment-${method.id}`}
                    >
                      <Icon className="h-5 w-5" />
                      {method.label}
                    </Button>
                  );
                })}
              </div>
            </section>
          )}
          {actionReservation?.status !== "completed" && actionReservation?.status !== "no_show" && (
            <Button
              variant="outline"
              onClick={markNoShow}
              className="w-full"
              data-testid="appointment-action-no-show"
            >
              <UserX className="mr-2 h-4 w-4" />
              Gelmedi
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}